/**
 * ConnectionsPanel — list of connected finance sources for ONE
 * capability (or a mixed set), composed of :class:`FinanceConnectionRow`
 * rows.
 *
 * Empty state: when the filtered set is empty, surface a clear
 * "Collega" CTA pointing at the integrations hub so the user doesn't
 * see a void after activating the tab.
 */

import { LinkIcon, Plug } from "lucide-react";
import type * as React from "react";
import { Plural, Trans } from "@lingui/react/macro";

import { Button, PencilCard, cn } from "@ando/ui";

import type { FinanceConnection } from "@/features/workspace/hooks/useFinance";

import { FinanceConnectionRow } from "./FinanceConnectionRow";


export interface ConnectionsPanelProps {
  readonly title: string;
  readonly connections: readonly FinanceConnection[];
  readonly emptyTitle: string;
  readonly emptyDescription: string;
  readonly onConnect: () => void;
  /** Total count (across capabilities) — used in the header counter. */
  readonly totalConnected?: number;
  readonly className?: string;
}


export const ConnectionsPanel: React.FC<ConnectionsPanelProps> = ({
  title,
  connections,
  emptyTitle,
  emptyDescription,
  onConnect,
  totalConnected,
  className,
}) => (
  <PencilCard as="section" className={cn("mb-4 p-5", className)}>
    <header className="mb-3.5 flex items-end justify-between gap-3">
      <div className="min-w-0">
        <div className="mb-2 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
          {title}
        </div>
        <div className="font-semibold text-[15px] text-ink-1">
          {connections.length === 0 ? (
            <Trans>Nessun collegamento</Trans>
          ) : (
            <Plural
              value={connections.length}
              one="# collegamento"
              other="# collegamenti"
            />
          )}
          {typeof totalConnected === "number" && totalConnected !== connections.length ? (
            <span className="ml-1.5 text-[11px] text-ink-3 tabular-nums">
              · <Trans>{totalConnected} attivi in totale</Trans>
            </span>
          ) : null}
        </div>
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={onConnect}
        className="gap-1.5"
      >
        <LinkIcon size={12} strokeWidth={2} aria-hidden />
        {connections.length === 0 ? <Trans>Collega</Trans> : <Trans>Aggiungi</Trans>}
      </Button>
    </header>
    {connections.length === 0 ? (
      <div className="flex items-start gap-3 rounded-lg border border-hairline border-dashed px-4 py-4">
        <Plug
          size={18}
          strokeWidth={1.75}
          aria-hidden
          className="mt-0.5 shrink-0 text-ink-3"
        />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-[12.5px] text-ink-1">
            {emptyTitle}
          </div>
          <p className="mt-0.5 text-[11.5px] text-ink-3 leading-snug">
            {emptyDescription}
          </p>
        </div>
      </div>
    ) : (
      <ul className="-mx-5 flex flex-col" aria-label={title}>
        {connections.map((c, idx) => (
          <FinanceConnectionRow key={c.id} connection={c} index={idx} />
        ))}
      </ul>
    )}
  </PencilCard>
);
ConnectionsPanel.displayName = "ConnectionsPanel";
